<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { hrAttendanceReportApi, hrUsersApi } from '$lib/api/hr';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { session } from '$lib/state/session.svelte';
  import { APP_TZ, reportDateRange, todayISO } from '$lib/utils/appDate';
  import type { HRUser, ReportGroup } from '$lib/types';

  const groups: { value: ReportGroup; label: string }[] = [
    { value: '', label: 'Unassigned' },
    { value: 'office', label: 'Office' },
    { value: 'avalahalli', label: 'Avalahalli' },
    { value: 'factory', label: 'Hoskote Factory' },
    { value: 'sales_field', label: 'Sales & Field' }
  ];
  let period = $state<'weekly' | 'monthly'>('weekly');
  const today = todayISO();
  let date = $state(today);
  let month = $state(today.slice(0, 7));
  const reportDate = $derived(period === 'monthly' ? (month ? `${month}-01` : '') : date);
  const bounds = $derived(reportDateRange(period, reportDate));
  let downloading = $state(false);
  let downloadError = $state('');
  let assignmentsOpen = $state(false);
  let search = $state('');
  let status = $state('active');
  let groupFilter = $state('all');
  let drafts = $state<Record<number, ReportGroup>>({});
  let saving = $state<number | null>(null);
  const queryClient = useQueryClient();
  const users = createQuery(() => ({
    queryKey: ['hr-users'], queryFn: () => hrUsersApi.list(), enabled: assignmentsOpen
  }));
  const eligible = $derived((users.data ?? []).filter((u) => !u.attendance_exempt));
  const unassigned = $derived(eligible.filter((u) => !u.report_group));
  const visible = $derived(eligible.filter((u) =>
    (status === 'all' || u.active === (status === 'active')) &&
    (groupFilter === 'all' || (u.report_group || '') === groupFilter) &&
    `${u.name} ${u.email}`.toLowerCase().includes(search.trim().toLowerCase())
  ));

  async function download() {
    if (!bounds || downloading) return;
    downloading = true;
    downloadError = '';
    try {
      await hrAttendanceReportApi.downloadPDF(period, reportDate);
      toast.success('Attendance PDF downloaded.');
    } catch (err) {
      downloadError = getErrorMessage(err);
    } finally {
      downloading = false;
    }
  }

  async function save(user: HRUser) {
    if (saving !== null || drafts[user.id] === undefined || (user.role === 'admin' && !session.isAdmin)) return;
    saving = user.id;
    try {
      const updated = await hrUsersApi.update(user.id, { report_group: drafts[user.id] });
      queryClient.setQueryData<HRUser[]>(['hr-users'], (rows) => rows?.map((u) => u.id === updated.id ? updated : u));
      delete drafts[user.id];
      await queryClient.invalidateQueries({ queryKey: ['hr-users'] });
      toast.success('Report home group updated.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      saving = null;
    }
  }
</script>

<section class="tym-card my-5 p-5" aria-labelledby="attendance-report-title">
  <h2 id="attendance-report-title" class="tym-section-title text-base">Attendance PDF report</h2>
  <p class="tym-section-sub mb-4">Calendar reports by home group — separate from the rolling insights below.</p>
  <div class="flex flex-wrap items-end gap-3">
    <label class="text-sm">Report period
      <select class="input mt-1" bind:value={period} disabled={downloading}>
        <option value="weekly">Weekly</option><option value="monthly">Monthly</option>
      </select>
    </label>
    {#if period === 'monthly'}
      <label class="text-sm">Calendar month
        <input class="input mt-1" aria-label="Report date" type="month" bind:value={month} disabled={downloading} />
      </label>
    {:else}
      <label class="text-sm">Any date in the week
        <input class="input mt-1" aria-label="Report date" type="date" bind:value={date} disabled={downloading} />
      </label>
    {/if}
    <button type="button" class="btn-primary" disabled={!bounds || downloading} onclick={download}>
      {downloading ? 'Downloading…' : 'Download PDF'}
    </button>
  </div>
  <p class="tym-section-sub mt-3" aria-live="polite">
    {#if bounds}
      {period === 'weekly' ? 'Monday–Sunday' : 'Calendar month'}: <strong>{bounds.from} – {bounds.to}</strong> (inclusive, {APP_TZ}).
    {:else}Choose a valid date to see the report range.{/if}
  </p>
  {#if downloadError}<p class="tym-error-state" role="alert">{downloadError}</p>{/if}

  <details class="mt-5 border-t pt-4" bind:open={assignmentsOpen}>
    <summary class="cursor-pointer font-medium text-navy">Manage report home groups</summary>
    <p class="tym-section-sub my-3">All employee activity is counted once under their current home group, not their punch location. Changing a group also changes how historical reports are grouped. HR can assign any employee to any group, including Sales &amp; Field. Attendance-exempt users are excluded from this list.</p>
    {#if users.isLoading}
      <p class="tym-loading-state">Loading employees…</p>
    {:else if users.isError}
      <div class="tym-error-state">Could not load employees. <button type="button" class="btn-secondary" onclick={() => users.refetch()}>Retry</button></div>
    {:else if users.data}
      {#if unassigned.length}
        <p class="mb-3 text-sm text-amber-700" role="status">Warning: {unassigned.length} employees have no home group ({unassigned.filter((u) => u.active).length} active). Assign them before relying on group totals.</p>
      {/if}
      <div class="mb-3 flex flex-wrap gap-3">
        <input class="input" aria-label="Search employees" placeholder="Search name or email" bind:value={search} />
        <select class="input" aria-label="Employee status" bind:value={status}>
          <option value="active">Active employees</option><option value="inactive">Inactive employees</option><option value="all">All employees</option>
        </select>
        <select class="input" aria-label="Filter home group" bind:value={groupFilter}>
          <option value="all">All home groups</option>
          {#each groups as group}<option value={group.value}>{group.label}</option>{/each}
        </select>
      </div>
      <p class="tym-section-sub mb-2">{visible.length} employees shown. Save each changed row.</p>
      <div class="max-h-96 overflow-y-auto divide-y">
        {#each visible as user (user.id)}
          <div class="flex flex-wrap items-center gap-3 py-3">
            <div class="min-w-0 flex-1 basis-48">
              <p class="break-words font-medium text-navy">{user.name || user.email} <span class="tym-badge">{user.active ? 'Active' : 'Inactive'}</span></p>
              <p class="tym-section-sub break-words">{user.email}</p>
              {#if !user.report_group}<p class="text-xs text-amber-700">Unassigned — needs a home group</p>{/if}
              {#if user.role === 'admin' && !session.isAdmin}<p class="tym-section-sub">Only an admin can change this employee’s group.</p>{/if}
            </div>
            <select class="input !w-auto max-w-full" aria-label={`Report group for ${user.name}`}
              value={drafts[user.id] ?? user.report_group ?? ''} disabled={saving === user.id || (user.role === 'admin' && !session.isAdmin)}
              onchange={(event) => { drafts[user.id] = event.currentTarget.value as ReportGroup; }}>
              {#each groups as group}<option value={group.value}>{group.label}</option>{/each}
            </select>
            <button type="button" class="btn-secondary" aria-label={`Save home group for ${user.name || user.email}`}
              disabled={saving !== null || (user.role === 'admin' && !session.isAdmin) || drafts[user.id] === undefined || drafts[user.id] === (user.report_group || '')}
              onclick={() => save(user)}>{saving === user.id ? 'Saving…' : 'Save'}</button>
          </div>
        {:else}<p class="tym-empty-state">No employees match these filters.</p>{/each}
      </div>
    {/if}
  </details>
</section>
